import { useWindowSize } from 'hooks/useWindowSize';
import { useEffect, useRef } from 'react';
import Flickity from '../../../flickity/flickity';
import { getCSSVar, isMobileWidth } from 'utils/helpers';
import { MediaBlock } from '../mediaBlock/MediaBlock';
import { DefaultBlockProps, DefaultMediaBlockProps } from '../switchBlock/SwitchBlock';

import style from './GalleryBlock.module.scss';
import { DESKTOP_BREAKPOINT } from 'pages/_app';

export type BlocksGalleryType = DefaultBlockProps & {
  block_type: 'gallery';
  media: DefaultMediaBlockProps[];
};

export const GalleryBlock = ({ media }: BlocksGalleryType): JSX.Element => {
  const canWrapRef = useRef<boolean>(true);
  const flickityRef = useRef<any>();

  const windowSize = useWindowSize();
  const isMobileScreen = isMobileWidth(windowSize.width);

  useEffect(() => {
    //  flickity doesnt render the looped image on mount, a quick resize fixes this glitch
    flickityRef.current && flickityRef.current.resize();
  }, [media]);

  const images = media.map((mediaItem, index) => {
    const mobile = mediaItem.phone_image
      ? {
          src: mediaItem.phone_image,
          width: mediaItem.phone_image_width,
          height: mediaItem.phone_image_height,
        }
      : undefined;

    const width = (isMobileScreen && mediaItem.phone_image) ? mediaItem.phone_image_width : mediaItem.image_width;
    const height = (isMobileScreen && mediaItem.phone_image) ? mediaItem.phone_image_height : mediaItem.image_height;
    const aspectRatio = `${width} / ${height}`;
    const containerStyle = { aspectRatio };

    return (
      <div className={`${style.slide} block-gallery`} key={index}>
        <div className={`${style.imageContainer} js-gallery-image-block`} style={containerStyle}>
          <MediaBlock
            block_type="media"
            media={mediaItem}
            layout="fill"
            objectFit="cover"
            priority={true}
            sizes={`${DESKTOP_BREAKPOINT} 90vh,
            100vw`}
          />
          {/* <ImageBlock
            desktop={{
              layout: 'fill',
              objectFit: 'cover',
              src: mediaItem.image,
              priority: true,
            }}
            mobile={mobile}
            alt={mediaItem.alt_text}
          /> */}
          {mediaItem.caption && <p className="caption">{mediaItem.caption}</p>}
        </div>
      </div>
    );
  });

  useEffect(() => {
    const totalWidthOfAllSlides = media.slice(0, -1).reduce((previousValue, currentMedia) => {
      const aspectRatioOfDesktopImage = currentMedia.image_width / currentMedia.image_height;

      const renderedImageHeight =
        (getCSSVar('--galleryBlock-slide-height') / 100) * windowSize.height;

      // const viewportHeight = 0.9 * windowSize.height;
      const newWidth = renderedImageHeight * aspectRatioOfDesktopImage;
      return previousValue + newWidth;
    }, 0);
    const canWrap = totalWidthOfAllSlides > windowSize.width;
    // mobile use width instead of height, so we can just check the amount
    canWrapRef.current = isMobileScreen ? media.length >= 3 : canWrap;
  }, [windowSize]);

  const canWrapAround = () => {
    // DEV NOTE: flickity doesn't update it's props when React re-renders. Therefore we need to work with Refs because the flickity callbacks are only set once on mount!

    return canWrapRef.current;
  };

  return (
    <>
      <div className={`${style.viewport} block-gallery`}>
        {/* DEV NOTE: due to a bug in flickity/react (?) using the 'watchCSS' methid doesn't render any children when rendered on a disabled state first */}
        {/* {isMobileScreen ? (
          <div>{images}</div>
        ) : ( */}
        <>
          <Flickity
            className={style.carousel}
            flickityRef={(c) => {
              flickityRef.current = c;
            }}
            options={{
              pageDots: true,
              prevNextButtons: false,
              cellAlign: 'center',
              // groupCells: '100%',
              // @ts-ignore
              wrapAround: canWrapAround,
              contain: true, // NOTE: has no effect if wrapAround is true
            }}
            // IMPORTANT: not recommended but on this specfic project we can't have it render in a portal
            static
          >
            {images}
          </Flickity>
        </>
        {/* )} */}
      </div>
    </>
  );
};
